"use client";

import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Link } from "react-router-dom";

const options: any = {
  renderMark: {
    [MARKS.CODE]: (text: string) => (
      <pre className="bg-gray-800 text-white rounded-md p-4 overflow-auto my-4">
        <code className="text-sm">{text}</code>
      </pre>
    ),
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      const isCode = node.content.some((item: any) =>
        item.marks?.some((mark: any) => mark.type === "code")
      );
      if (isCode) {
        return (
          <pre className="bg-[#666481] text-white rounded-md p-4 overflow-auto my-4">
            <code>{children}</code>
          </pre>
        );
      }

      return (
        <p className="text-gray-500 font-[300] leading-[36px] text-lg mb-4">
          {children}
        </p>
      );
    },

    [BLOCKS.HEADING_1]: (_: any, children: any) => (
      <h1 className="text-4xl font-bold text-black mb-6">{children}</h1>
    ),
    [BLOCKS.HEADING_2]: (_: any, children: any) => (
      <h2 className="text-3xl font-semibold text-black mb-5">{children}</h2>
    ),
    [BLOCKS.HEADING_3]: (_: any, children: any) => (
      <h3 className="text-2xl font-medium text-black mb-4">{children}</h3>
    ),
    [BLOCKS.HEADING_4]: (_: any, children: any) => (
      <h4 className="text-xl font-medium text-black mb-3">{children}</h4>
    ),
    [BLOCKS.HEADING_5]: (_: any, children: any) => (
      <h5 className="text-lg font-medium text-black mb-2">{children}</h5>
    ),

    [BLOCKS.UL_LIST]: (_: any, children: any) => (
      <ul className="list-disc pl-6 mb-4">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_: any, children: any) => (
      <ol className="list-decimal pl-6 mb-4">{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (_: any, children: any) => (
      <li className="text-gray-700 text-lg mb-2">{children}</li>
    ),

    [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
      if (node.data.target.sys.contentType.sys.id === "videoEmbed") {
        return (
          <iframe
            className="w-full h-[400px] rounded-md border border-gray-200 my-6"
            src={node.data.target.fields.embedUrl}
            title={node.data.target.fields.title}
            allowFullScreen
          />
        );
      }
    },

    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      const imageUrl = node.data.target.fields.file.url.startsWith("//")
        ? `https:${node.data.target.fields.file.url}`
        : node.data.target.fields.file.url;

      return (
        <div className="my-8 py-4">
          <img
            src={imageUrl}
            alt={node.data.target.fields.title}
            className="rounded-md object-cover w-[400px] h-[300px] ml-[-2px]"
          />
        </div>
      );
    },

    [INLINES.ENTRY_HYPERLINK]: (node: any) => {
      if (node.data.target.sys.contentType.sys.id === "blogs") {
        return (
          <Link
            to={`/insights-stories/${encodeURIComponent(
              node.data.target.fields.slug
            )}`}
            className="text-blue-500 underline px-1"
          >
            {node.data.target.fields.title}
          </Link>
        );
      }
    },

    [INLINES.HYPERLINK]: (node: any) => {
      const text = node.content.find(
        (item: any) => item.nodeType === "text"
      )?.value;
      const url =
        node.data.uri.startsWith("http://") ||
        node.data.uri.startsWith("https://")
          ? node.data.uri
          : `https://${node.data.uri}`;
      return (
        <a
          href={url}
          className="text-blue-500 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {text}
        </a>
      );
    },
  },
};

const RichText = ({ content }: { content: any }) => {
  return <div>{documentToReactComponents(content, options)}</div>;
};

export default RichText;
